<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BranchTransferModal from 'dashboard/components/widgets/modal/BranchTransferModal.vue';
import BranchesAPI from 'dashboard/api/branches';
import { usePolicy } from 'dashboard/composables/usePolicy';
import SendToFunnelButton from 'dashboard/components-next/SalesFunnel/SendToFunnelButton.vue';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
    BranchTransferModal,
    SendToFunnelButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    const { checkPermissions } = usePolicy();
    return {
      agentsList,
      checkPermissions,
    };
  },
  data() {
    return {
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
      showBranchTransferModal: false,
      pendingBranch: null,
      transferableBranches: [],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
      branches: 'branches/getBranches',
      myBranches: 'branches/getMyBranches',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      accountId: 'getCurrentAccountId',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    filteredTeamsByBranch() {
      const isAdmin = this.currentUser.role === 'administrator';
      if (isAdmin || !this.isBranchManagementEnabled) return this.teams;

      const myBranchIds = this.myBranches.map(b => b.id);
      return this.teams.filter(
        team => !team.branch_id || myBranchIds.includes(team.branch_id)
      );
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.filteredTeamsByBranch,
        ];
      }
      return this.filteredTeamsByBranch;
    },
    hasAnAssignedBranch() {
      return !!this.currentChat?.meta?.branch;
    },
    isBranchManagementEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        'branch_management'
      );
    },
    canTransferBranch() {
      const isAdministrator = this.currentUser.role === 'administrator';
      return isAdministrator || this.checkPermissions(['branch_transfer']);
    },
    branchesList() {
      const listToUse = this.transferableBranches.length
        ? this.transferableBranches
        : this.branches;
      if (this.hasAnAssignedBranch) {
        return [
          { id: 0, name: this.$t('SIDEBAR.BRANCH_SELECTOR.NONE') },
          ...listToUse,
        ];
      }
      return listToUse;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedBranch: {
      get() {
        return this.currentChat.meta.branch;
      },
      set(branchData) {
        const conversationId = this.currentChat.id;
        const branchId = branchData.branch ? branchData.branch.id : 0;
        this.$store
          .dispatch('assignBranch', {
            conversationId,
            branchId,
            team: branchData.team,
            agent: branchData.agent,
          })
          .then(() => {
            const isAdministrator = this.currentUser.role === 'administrator';
            const hasBranchAccess = this.myBranches.some(
              b => b.id === branchId
            );

            if (branchId !== 0 && !isAdministrator && !hasBranchAccess) {
              this.$store.commit('DELETE_CONVERSATION', conversationId);
              if (
                this.$route.params.conversation_id === conversationId.toString()
              ) {
                this.$router.push({ name: 'home' });
              }
            }
          })
          .catch(error => {
            const errorMessage =
              error?.response?.data?.error || error?.response?.data?.message;
            if (errorMessage) {
              useAlert(errorMessage);
            }
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
  },
  mounted() {
    if (this.isBranchManagementEnabled) {
      this.fetchTransferableBranches();
    }
  },
  methods: {
    async fetchTransferableBranches() {
      try {
        const response = await BranchesAPI.getTransferable();
        this.transferableBranches = response.data;
      } catch (error) {
        // Ignore error
      }
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignBranch(selectedItemBranch) {
      const isSameBranch =
        this.assignedBranch && this.assignedBranch.id === selectedItemBranch.id;

      if (isSameBranch || !selectedItemBranch || selectedItemBranch.id === 0) {
        // Unassign branch, keep team and agent
        this.assignedBranch = {
          branch: null,
          team: this.assignedTeam,
          agent: this.assignedAgent,
        };
      } else {
        this.pendingBranch = selectedItemBranch;
        this.showBranchTransferModal = true;
      }
    },

    onConfirmBranchTransfer(data) {
      this.assignedBranch = {
        branch: this.pendingBranch,
        team: data.team,
        agent: data.agent,
      };
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div v-if="isBranchManagementEnabled">
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.BRANCH_LABEL')"
      />
      <MultiselectDropdown
        :options="branchesList"
        :selected-item="assignedBranch"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.BRANCH')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.BRANCH')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.BRANCH')
        "
        :disabled="!canTransferBranch"
        @select="onClickAssignBranch"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />

    <div class="mt-4">
      <SendToFunnelButton :conversation-id="Number(conversationId)" />
    </div>

    <BranchTransferModal
      v-if="showBranchTransferModal"
      :show="showBranchTransferModal"
      :target-branch="pendingBranch"
      @close="showBranchTransferModal = false"
      @confirm="onConfirmBranchTransfer"
    />
  </div>
</template>
